import { useApi } from './useApi.js';
import { fetchPage } from '../lib/api.js';
import { emToStars } from '../lib/emphasis.js';

/**
 * Built-in fallback copy for the fixed-layout pages — the text/banner that
 * used to be hard-coded in each page file (and previously lived in the
 * page_headers table + SiteSettings.bannerImages, now retired). Used while the
 * API is in flight, when a page row is unpublished/missing, or on a fresh DB.
 * Admin edits in the Pages resource override all of this.
 */
const HEADER_DEFAULTS = {
  about: {
    rawTitle: 'A school of <em>character</em>, since 1972',
    lede: "Five decades of educating the children of Dhaka — and preparing them for the world's leading universities.",
    metaDescription: 'About Maple Leaf International School — our history, values and approach.',
  },
  admission: {
    rawTitle: "Begin your child's <em>Maple Leaf</em> journey",
    lede: 'A simple, transparent admission process — designed to find the right fit for your child and our community.',
    metaDescription: 'Admission process, required documents and enquiry form for Maple Leaf International School, Dhanmondi.',
  },
  contact: {
    rawTitle: 'Get in <em>touch</em>',
    lede: "We're open every day from 8:00 AM to 7:00 PM, except Friday. Drop in, call, or write — we're glad to hear from you.",
    metaDescription: 'Visit, call or write to Maple Leaf International School, Dhanmondi, Dhaka.',
  },
  facilities: {
    rawTitle: 'Spaces built for <em>learning</em>',
    lede: 'Two campuses in Dhanmondi — purpose-built for every stage of school life.',
    metaDescription: 'Two purpose-built campuses in Dhanmondi — libraries, science labs, sports facilities, an auditorium and a registered IELTS centre.',
  },
  alumni: {
    rawTitle: 'Once a Maple Leaf, <em>always</em> a Maple Leaf',
    lede: 'Our alumni community spans five decades and six continents. Join us.',
    metaDescription: 'Alumni of Maple Leaf International School — five decades, six continents.',
  },
  career: {
    rawTitle: 'Build your career <em>with us</em>',
    lede: "Teach, mentor and grow at one of Bangladesh's leading English Medium schools.",
    metaDescription: 'Career and teaching opportunities at Maple Leaf International School.',
  },
  events: {
    rawTitle: 'School <em>events</em>',
    lede: 'Concerts, sports days, parent meetings, fairs and quiet milestones — all part of the school year.',
    metaDescription: 'Upcoming and recent events at Maple Leaf International School.',
  },
  achievements: {
    rawTitle: 'Student <em>achievements</em>',
    lede: 'Celebrating the academic, sporting and creative successes of Maple Leaf students.',
    metaDescription: 'Academic, sporting and creative achievements of our students.',
  },
  calendar: {
    rawTitle: 'Academic <em>calendar 2025–26</em>',
    lede: 'Term dates, breaks and key milestones — at a glance.',
    metaDescription: 'Term dates, breaks and key milestones for the academic year.',
  },
  news: {
    rawTitle: 'Latest <em>news</em>',
    lede: 'Notices, stories and updates from across the school.',
    metaDescription: 'School notices, stories and recent updates.',
  },
  photos: {
    rawTitle: 'Photo <em>gallery</em>',
    lede: 'Glimpses of life at Maple Leaf — classrooms, fields, stages and corridors.',
    metaDescription: 'Photo gallery — moments from classrooms, fields, stages and corridors.',
  },
};

const BANNER_DEFAULTS = {
  about:        'https://images.unsplash.com/photo-1562774053-701939374585?w=2000&q=80',
  admission:    'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=2000&q=80',
  contact:      'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=2000&q=80',
  news:         'https://images.unsplash.com/photo-1577896851231-70ef18881754?w=2000&q=80',
  events:       'https://images.unsplash.com/photo-1466637574441-749b8f19452f?w=2000&q=80',
  achievements: 'https://images.unsplash.com/photo-1606166325683-e6deb697d301?w=2000&q=80',
  photos:       'https://images.unsplash.com/photo-1568667256549-094345857637?w=2000&q=80',
  alumni:       'https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=2000&q=80',
  career:       'https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=2000&q=80',
  calendar:     'https://images.unsplash.com/photo-1509062522246-3755977927d7?w=2000&q=80',
  facilities:   'https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=2000&q=80',
};

/**
 * Unified page accessor — the single source of truth for a page's header,
 * banner and SEO. Reads the `pages` row for `slug` and normalises it into a
 * flat shape, falling back to the built-in defaults above so a
 * missing/loading/unpublished row never breaks the UI.
 *
 *   const page = usePage('facilities');
 *   <Seo title="Facilities" description={page.metaDescription} />
 *   <PageHeader headline={page.headline} lede={page.lede} image={page.banner} />
 */
export function usePage(slug) {
  const { data, loading, error } = useApi(
    () => fetchPage(slug).catch((e) => {
      if (e?.response?.status === 404) return null; // unpublished/missing → use defaults
      throw e;
    }),
    [slug],
  );

  const hd = HEADER_DEFAULTS[slug] || {};
  const fallbackHeadline = emToStars(hd.rawTitle || '');
  const fallbackBanner = BANNER_DEFAULTS[slug] || BANNER_DEFAULTS.about;

  return {
    loading,
    error,
    raw: data,
    template: data?.template || 'content',
    title: data?.title || '',
    // Display H1 (asterisk emphasis): explicit headline → slug default → plain title.
    headline: data?.headline || fallbackHeadline || data?.title || '',
    lede: data?.lede || hd.lede || '',
    metaDescription: data?.metaDescription || hd.metaDescription || '',
    banner: data?.heroImage || fallbackBanner,
    heroImageFrame: !!data?.heroImageFrame,
    imageCaptionTitle: data?.imageCaptionTitle || '',
    imageCaptionSubtitle: data?.imageCaptionSubtitle || '',
    body: data?.body || '',
  };
}
